import { z } from 'zod'
import {
  FieldType,
  LocaleCode,
  ParticipantKind,
  RoutingMode,
  DeliveryMode,
  SenderOrder,
} from './enums.js'
import { GeometryCoord, GeometrySize } from './geometry.js'
import { FieldDefault, FieldFormatting, FieldOwner } from './field-attributes.js'
import { FieldValidation } from './validation.js'
import { SCHEMA_VERSION, HASH_STRING_PATTERN } from './version.js'

/** Role placeholder on a template or draft — PRD §12.4. */
export const Role = z
  .object({
    id: z.string().min(1).max(64),
    label: z.string().min(1).max(200),
    /**
     * What the person in this role does. Absent means a signer, so every role written before this
     * key existed keeps its meaning; a CC role receives a copy and owns no fields (PRD §6.1).
     */
    kind: ParticipantKind.optional(),
  })
  .strict()
export type Role = z.infer<typeof Role>

/**
 * Field overlay entry.
 * Geometry field names match the PRD §12.4 golden example exactly.
 *
 * Part Three D7 adds the field attribute set below (owner, label, tooltip, editable
 * status, default, formatting, validation). Every D7 addition is optional, so the
 * §12.4 golden example and every manifest written before v1.7 keep validating
 * unchanged. Text and tooltip maxima are register values applied by
 * `validateFieldLimits` (`field-limits.ts`), never literals here.
 */
export const Field = z
  .object({
    id: z.string().min(1).max(64),
    type: FieldType,
    roleId: z.string().min(1).max(64).optional(),
    page_index: GeometryCoord,
    x: GeometryCoord,
    y: GeometryCoord,
    w: GeometrySize,
    h: GeometrySize,
    required: z.boolean(),
    maxLength: z.number().int().positive().max(100_000).optional(),
    locked: z.boolean().optional(),
    /** D7 attributes (optional, additive). */
    owner: FieldOwner.optional(),
    label: z.string().optional(),
    tooltip: z.string().optional(),
    editable: z.boolean().optional(),
    default_value: FieldDefault.optional(),
    formatting: FieldFormatting.optional(),
    validation: FieldValidation.optional(),
    /** D9 declaration for a FORMULA field; evaluated at completion (S1.9). */
    expression: z.string().min(1).max(500).optional(),
  })
  .strict()
export type Field = z.infer<typeof Field>

/** Immutable source + field overlay for a template or draft — PRD §12.4/§12.5. */
export const FieldManifestV1 = z
  .object({
    schema_version: z.literal(SCHEMA_VERSION),
    /** page_index is zero-based; geometry is millionths of the normalized crop box. */
    page_index_convention: z.literal('zero_based_millionths'),
    source_sha256: z.string().regex(HASH_STRING_PATTERN),
    fields: z.array(Field).max(500),
    roles: z.array(Role).max(50),
  })
  .strict()
export type FieldManifestV1 = z.infer<typeof FieldManifestV1>

/** PRD §12.4 golden template example (camelCase keys as specified). */
export const TemplateV1 = z
  .object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    templateId: z.string().min(1).max(64),
    title: z.string().min(1).max(200),
    sourceHash: z.string().min(1),
    roles: z.array(Role),
    fields: z.array(Field),
  })
  .strict()
export type TemplateV1 = z.infer<typeof TemplateV1>

/** External recipient specification at draft/instantiation time — PRD §6.1/§12.1. */
export const RecipientSpec = z
  .object({
    role_id: z.string().min(1).max(64).optional(),
    kind: ParticipantKind,
    name: z.string().min(1).max(200),
    email: z.email().max(320).optional(),
    order_index: z.number().int().nonnegative(),
    locale_code: LocaleCode.optional(),
  })
  .strict()
export type RecipientSpec = z.infer<typeof RecipientSpec>

/** Consent evidence reference — PRD §7.4/§10.3. */
export const ConsentRef = z
  .object({
    consent_version: z.string().min(1).max(64),
    consent_locale: LocaleCode,
  })
  .strict()
export type ConsentRef = z.infer<typeof ConsentRef>

/** Reminder schedule policy — PRD §12.4 example. Caps still enforced from register. */
export const ReminderPolicy = z
  .object({
    first_after_days: z.number().int().positive().max(30),
    repeat_every_days: z.number().int().positive().max(30),
    max_reminders: z.number().int().nonnegative().max(20),
  })
  .strict()
export type ReminderPolicy = z.infer<typeof ReminderPolicy>

/** Workflow configuration referencing a template — PRD §12.4. */
export const WorkflowV1 = z
  .object({
    schema_version: z.literal(SCHEMA_VERSION),
    template_id: z.string().min(1).max(64),
    title: z.string().min(1).max(200),
    routing_mode: RoutingMode,
    role_order: z.array(z.string().min(1).max(64)),
    sender_order: SenderOrder,
    delivery_mode: DeliveryMode,
    subject_template: z.string().max(200),
    message_template: z.string().max(2000),
    reminder_policy: ReminderPolicy,
    expiration_days: z.number().int().positive().max(90),
    completion_distribution: z.boolean(),
    cc_rules: z
      .array(
        z.object({
          role_id: z.string().min(1).max(64),
          required: z.boolean(),
        }),
      )
      .max(5),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
  })
  .strict()
export type WorkflowV1 = z.infer<typeof WorkflowV1>
